/**
 * Pharmacy Order Service — API-ready CRUD for ePrescriptions and bulk orders.
 *
 * Uses the PharmacyOrder model from data/models/PharmacyOrder.js.
 * Persistence is localStorage; swap method bodies for fetch() when a backend
 * is available.  Return contract: { success, data?, error? }.
 */

import LocalStorageService from '../storage/LocalStorageService';
import { createAuditEntry } from '../../utils/auditLogger';
import { createPharmacyOrder, ORDER_STATUS } from '../../data/models/PharmacyOrder';

const STORE_KEY = 'pharmacy_orders_v1';
const LOG_PREFIX = '[PharmacyOrderService]';

function _audit(action, entityId, oldValue, newValue, userId) {
  createAuditEntry({
    eventType: 'data_modification',
    entityType: 'pharmacy_order',
    entityId,
    action,
    oldValue,
    newValue,
    userId,
  });
}

class PharmacyOrderService {
  static async getAll(filters = {}) {
    const all = LocalStorageService.get(STORE_KEY, []);
    let filtered = all;
    if (filters.status) filtered = filtered.filter((o) => o.status === filters.status);
    if (filters.residentId) filtered = filtered.filter((o) => o.residentId === filters.residentId);
    if (filters.pharmacy) filtered = filtered.filter((o) => o.pharmacy === filters.pharmacy);
    return { success: true, data: filtered };
  }

  static async getById(id) {
    const all = LocalStorageService.get(STORE_KEY, []);
    const found = all.find((o) => o.id === id);
    if (!found) return { success: false, error: 'Order not found' };
    return { success: true, data: found };
  }

  static async create(orderData, userId = 'system') {
    if (!orderData.residentName && !orderData.residentId) {
      return { success: false, error: 'Resident is required' };
    }
    const order = createPharmacyOrder({
      ...orderData,
      status: orderData.status || ORDER_STATUS.PENDING_APPROVAL,
    });
    const all = LocalStorageService.get(STORE_KEY, []);
    all.unshift(order);
    LocalStorageService.set(STORE_KEY, all);
    _audit('create', order.id, null, order, userId);
    console.log(LOG_PREFIX, 'Created order', order.id);
    return { success: true, data: order };
  }

  static async updateStatus(id, newStatus, userId = 'system', extra = {}) {
    const all = LocalStorageService.get(STORE_KEY, []);
    const idx = all.findIndex((o) => o.id === id);
    if (idx === -1) return { success: false, error: 'Order not found' };

    const prev = { ...all[idx] };
    all[idx] = {
      ...all[idx],
      status: newStatus,
      ...extra,
      updatedAt: new Date().toISOString(),
    };
    if (newStatus === ORDER_STATUS.COMPLETED || newStatus === ORDER_STATUS.DELIVERED) {
      all[idx].deliveredAt = all[idx].deliveredAt || new Date().toISOString();
    }
    LocalStorageService.set(STORE_KEY, all);
    _audit('update', id, prev, all[idx], userId);
    console.log(LOG_PREFIX, 'Updated order', id, '->', newStatus);
    return { success: true, data: all[idx] };
  }

  static async cancel(id, userId = 'system') {
    return PharmacyOrderService.updateStatus(id, ORDER_STATUS.CANCELLED, userId);
  }

  static async getStats() {
    const all = LocalStorageService.get(STORE_KEY, []);
    const stats = {
      total: all.length,
      pending: all.filter((o) => o.status === ORDER_STATUS.PENDING_APPROVAL).length,
      sent: all.filter((o) => o.status === ORDER_STATUS.SENT).length,
      completed: all.filter((o) => o.status === ORDER_STATUS.COMPLETED).length,
      cancelled: all.filter((o) => o.status === ORDER_STATUS.CANCELLED).length,
    };
    return { success: true, data: stats };
  }
}

export default PharmacyOrderService;
